import { Injectable } from '@angular/core';
import { Config } from './config';
import { EMPTY } from 'rxjs';
import { createScheduleCollectionRequest, ScheduleCollection, ScheduleCollectionDetails, ScheduleCollectionRequest, ScheduleCollectionResponse, ScheduleUpcomingCollectionResponse } from 'app/shared/models/schedule-collection';
import { HttpClient, HttpParams } from '@angular/common/http';
import { DEFAULT_START_PAGE, DEFAULT_PAGE_SIZE } from '../constant';
import {
    CollectionHistory,
    DataCommunicationDetails,
    GetCollectDetailsArgs,
    GetCollectHistoryArgs,
} from '../models/collection-history';
import { map } from 'rxjs/operators';
import { Selectable } from '../models/selectable';

@Injectable()
export class ScheduleCollectionService {
    public addDialogIsOpened = false;
    public collectionHistory: Array<CollectionHistory> = [];

    constructor(public http: HttpClient) {}

    public getUpcomingCollection(customerId: number) {
        let httpParams: HttpParams = new HttpParams();
        httpParams = httpParams.append('customerId', customerId.toString());

        return this.http.get<ScheduleUpcomingCollectionResponse>(Config.urls.collectsUpcoming, { params: httpParams }).pipe(
            map((res) => {
                if (res) {
                    return res.collectionstart;
                } else {
                    return null;
                }
            }),
        );
    }


    public getScheduleCollection(customerId: number) {
        return this.http.get<ScheduleCollectionResponse>(Config.serviceUrl + Config.urls.collectSchedules + `${customerId}`).pipe(
            map((response) => {
                if (response) {
                    return response.schedules;
                } else {
                    EMPTY;
                }
            }),
        );
    }
    public getLiteScheduledCollections(customerId: number) {
        return this.http.get<ScheduleCollection[]>(Config.serviceUrl + Config.urls.liteScheduledCollections + `${customerId}`);
    }

    public getAvailableLocations(customerId: number, scheduleId?: number) {
        let uri: string;
        if (scheduleId) {
            uri = `${Config.urls.availableLocations}?cid=${customerId}` + `&scheduleId=${scheduleId}`;
        } else {
            uri = `${Config.urls.availableLocations}?cid=${customerId}`;
        }
        return this.http.get<Selectable[]>(uri);
    }

    public getSelectedScheduleCollection(scheduleId: number) {
        return this.http.get<ScheduleCollectionDetails>(Config.serviceUrl + Config.urls.scheduledCollections + `${scheduleId}`);
    }

    public getCollectionHistory(params: GetCollectHistoryArgs) {
        // Converting Dates into string
        let startDate: string = null;
        let endDate: string = null;
        if (params.StartTime) {
            startDate = [
                params.StartTime.getMonth() + 1,
                params.StartTime.getDate(),
                params.StartTime.getFullYear(),
            ].join('-');
            delete params.StartTime;
        }
        if (params.EndTime) {
            endDate = [params.EndTime.getMonth() + 1, params.EndTime.getDate(), params.EndTime.getFullYear()].join('-');
            endDate = endDate + ' 23:59:59';
            delete params.EndTime;
        }
        // creating HttpParams for HttpClient request
        let httpParams: HttpParams = new HttpParams();

        // setting all the existing key value of param to HttpParams
        Object.keys(params).forEach(function (key) {
            if (key !== 'LocationGroupID' && params[key] != null) {
                httpParams = httpParams.append(key, params[key]);
            }
        });

        if(startDate) {
            httpParams = httpParams.append('StartTime', startDate);
        }

        if(endDate) {
            httpParams = httpParams.append('EndTime', endDate);
        }

        if (params.LocationGroupID > 0) {
            httpParams = httpParams.append('LocationGroupID', params.LocationGroupID.toString());
        }

        if (params.LocationIDs) {
            httpParams = httpParams.append('LocationIDs', params.LocationIDs.toString());
        }

        // add fixed param: includeTcpConnections=true
        httpParams = httpParams.append('includeTcpConnections', 'true');

        return this.http.get<Array<CollectionHistory>>(Config.urls.collectsHistory, { params: httpParams }).pipe(
            map((response) => {
                return response;
            }),
        );
    }
    public getCollectionDetails(startPage: number, pageSize: number, params: GetCollectDetailsArgs) {
        let startDate: string = null;
        let endDate: string = null;
        params.StartPage = startPage || DEFAULT_START_PAGE;
        params.PageSize = pageSize || DEFAULT_PAGE_SIZE;
        if (params.StartTime) {
            const m = params.StartTime.getMonth() + 1 > 9 ? params.StartTime.getMonth() + 1 : '0' + (params.StartTime.getMonth() + 1);
            const d = params.StartTime.getDate() > 9 ? params.StartTime.getDate() : '0' + params.StartTime.getDate();
            startDate = [params.StartTime.getFullYear(), m, d].join('-');
            startDate = startDate + 'T00:00:00';
            delete params.StartTime;
        }
        if (params.EndTime) {
            const m = params.EndTime.getMonth() + 1 > 9 ? params.EndTime.getMonth() + 1 : '0' + (params.EndTime.getMonth() + 1);
            const d = params.EndTime.getDate() > 9 ? params.EndTime.getDate() : '0' + params.EndTime.getDate();
            endDate = [params.EndTime.getFullYear(), m, d].join('-');
            endDate = endDate + 'T23:59:59';
            delete params.EndTime;
        }

        let httpParams: HttpParams = new HttpParams();
        Object.keys(params).forEach(function (key) {
            if (key !== 'LocationGroupID' && params[key] != null) {
                httpParams = httpParams.append(key, params[key]);
            }
        });

        if(startDate) {
            httpParams = httpParams.append('StartTime', startDate);
        }

        if(endDate) {
            httpParams = httpParams.append('EndTime', endDate);
        }

        if (params.LocationGroupID > 0) {
            httpParams = httpParams.append('LocationGroupID', params.LocationGroupID.toString());
        }
        if (params.LocationIDs) {
            httpParams = httpParams.append('LocationIDs', params.LocationIDs.toString());
        }
        if (params.monitorIds) {
            httpParams = httpParams.append('MonitorIds', params.monitorIds.toString());
        }
        return this.http.get<DataCommunicationDetails>(Config.urls.collectsDetails, { params: httpParams });
    }

    public postScheduleCollection(customerId: number, scheduleCollection: ScheduleCollectionRequest) {
        const params: ScheduleCollectionRequest = {
            alarmingFrequency: scheduleCollection.alarmingFrequency,
            daysToCollect: scheduleCollection.collectionDays,
            frequency: scheduleCollection.frequency,
            locations: scheduleCollection.locations,
            name: scheduleCollection.name,
            collectTime: scheduleCollection.collectTime,
        };

        return this.http.post<void>(Config.serviceUrl + Config.urls.collectSchedules + `${customerId}`, params);
    }

    public updateScheduleCollection(
        scheduleId: number,
        locations: Array<number>,
        scheduleName: string,
        frequency: number,
        alarmingFrequency: number,
        collectionDays: number[],
        collectTime: string,
    ) {
        const url = `api/collections/schedules/${scheduleId}`;

        const params = createScheduleCollectionRequest();
        params.name = scheduleName;
        params.frequency = frequency;
        params.locations = locations;
        params.daysToCollect = collectionDays;
        params.collectTime = collectTime;
        params.alarmingFrequency = alarmingFrequency || undefined;

        return this.http.put<void>(`${Config.serviceUrl}${url}`, params);
    }

    public deleteScheduleCollection(scheduleId: number) {
        return this.http.delete<void>(Config.serviceUrl + Config.urls.scheduledCollections + `${scheduleId}`);
    }

    public getScheduleByLocationId(customerId: number, locationId: number) {
        return this.http.get<ScheduleCollection>(Config.serviceUrl + Config.urls.scheduleByLocationId + `${customerId}` + `&locationId=${locationId}`);
    }
}
